import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { DriverEntity } from 'src/entities/Driver.entity';
import { GarageEntity } from 'src/entities/Garage.entity';
import { PlanningsDeliveryEntity, PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';
import { TemporaryTruckEntity } from 'src/entities/TemporaryTruckEntity';
import { TruckEntity } from 'src/entities/Truck.entity';

import { CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY } from './useConvenienceDispatchDeliveries.query';
import { usePlanningsDeliveriesCache, usePlanningsDeliveriesWithLegRunsCache, usePlanningsDriversCache, usePlanningsTrucksCache } from './usePlanningsCache';

const requestPath: (id?: number) => string = (id) => {
  const base = '/api/v4/master/temporary_trucks';

  if (!id) return base;

  return `${base}/${id}`;
};

type ResponseType = {
  garage: GarageEntity;
  trucks: TruckEntity[];
  drivers: DriverEntity[];
  deliveries: PlanningsDeliveryEntity[];
}

export const useTemporaryTruckMutation = () => {
  const queryClient = useQueryClient();
  const trucksCache = usePlanningsTrucksCache();
  const driversCache = usePlanningsDriversCache();
  const deliveriesCache = usePlanningsDeliveriesCache();
  const deliveriesWithLegRunsCache = usePlanningsDeliveriesWithLegRunsCache();

  return {
    create: useMutation((entity: TemporaryTruckEntity) => axios.post(requestPath(), entity), {
      onSuccess: (response: AxiosResponse<ResponseType>) => {
        const responseData = response.data;
        const garageCache = queryClient.getQueriesData({ queryKey: ['useGaragesQuery'] });
        garageCache.forEach(([queryCacheKey, cacheData]) => {
          if (cacheData && Array.isArray(cacheData)) {
            const typedCacheData = cacheData as GarageEntity[];
            const ids = typedCacheData.map((it) => it.id);
            const existingIndex = ids.indexOf(responseData.garage.id);

            let updatedCacheData: GarageEntity[];
            if (existingIndex !== -1) {
              updatedCacheData = [...typedCacheData];
              updatedCacheData[existingIndex] = responseData.garage;
            } else {
              updatedCacheData = [...typedCacheData, responseData.garage];
            }
            queryClient.setQueryData(queryCacheKey, updatedCacheData);
          }
        });

        void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
        trucksCache.addTrucks(responseData.trucks);
        driversCache.addDrivers(responseData.drivers);
        deliveriesCache.addDeliveriesByDateRange(responseData.deliveries);
        deliveriesWithLegRunsCache.addDeliveriesByDateRange(responseData.deliveries.map((it) => ({
          ...it,
          sameTimeWithShift: true,
          assignments: [],
          operations: [],
          shipmentOrderLegRuns: [],
          maximumLoadingCapacityQuantity: null,
        } as PlanningsDeliveryWithLegRunEntity)));
      }
    }),
  };
};
